import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, login, logout } = useAuth();
  const [mobile, setMobile] = React.useState(false);

  const linkClass = ({ isActive }) =>
    `label-mono link-underline transition-colors ${isActive ? "text-[var(--primary)]" : "text-[var(--text-secondary)] hover:text-[var(--text)]"}`;

  return (
    <header className="header-glass fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 divine-aura" data-testid="brand-link">
          <div className="w-10 h-10 border border-[var(--primary)] flex items-center justify-center divine-pulse bg-[var(--surface)]">
            <span className="font-sacred text-[var(--primary-hover)] text-xl leading-none glyph-breathe">ॐ</span>
          </div>
          <div className="leading-none">
            <div className="font-sacred text-[var(--text)] text-lg tracking-widest embossed-light">RUDRA ROAST</div>
            <div className="label-mono text-[8px] mt-1 text-[var(--primary)] whitespace-nowrap hidden sm:block">॥ strong · classic blend ॥</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/shop" className={linkClass} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/blend" className={linkClass} data-testid="nav-blend">Custom Blend</NavLink>
          <NavLink to="/story" className={linkClass} data-testid="nav-story">Story</NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">Control Room</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-4">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/account" className="label-mono text-[var(--text-secondary)] hover:text-[var(--primary)]" data-testid="nav-account">
                {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={logout} className="label-mono text-[var(--text-muted)] hover:text-[var(--primary)]" data-testid="logout-btn">Logout</button>
            </div>
          ) : (
            <button onClick={login} className="hidden md:block label-mono text-[var(--text-secondary)] hover:text-[var(--primary)]" data-testid="login-google-button">
              Sign in
            </button>
          )}
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 border border-[var(--border)] px-3 py-2 hover:border-[var(--primary)] transition-colors"
            data-testid="cart-toggle"
          >
            <ShoppingBag size={16} />
            <span className="label-mono text-[10px]">Cart</span>
            {count > 0 && (
              <span className="font-mono text-[10px] text-[var(--primary)]">[{count}]</span>
            )}
          </button>
          <button className="md:hidden" onClick={() => setMobile((v) => !v)} data-testid="mobile-menu">
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden border-t border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur">
          <div className="flex flex-col px-6 py-6 gap-4">
            <NavLink to="/shop" onClick={() => setMobile(false)} className="label-mono">Shop</NavLink>
            <NavLink to="/blend" onClick={() => setMobile(false)} className="label-mono">Custom Blend</NavLink>
            <NavLink to="/story" onClick={() => setMobile(false)} className="label-mono">Story</NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin" onClick={() => setMobile(false)} className="label-mono">Control Room</NavLink>
            )}
            {user ? (
              <>
                <NavLink to="/account" onClick={() => setMobile(false)} className="label-mono">Account</NavLink>
                <button onClick={logout} className="label-mono text-left text-[var(--text-muted)]">Logout</button>
              </>
            ) : (
              <button onClick={login} className="label-mono text-left" data-testid="mobile-login-btn">Sign in with Google</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
